import { ROUTE_NAMES } from '@/lib/enums';

/**
 * The depot onboarding tours: one declarative table, no per-page code, in the shape
 * `config/app.ts` already uses for the navigation menu.
 *
 * **Absence means no tour.** The depot section has 53 named routes and only the ones
 * listed here carry a tour; the rest are deliberately unlisted rather than being given
 * empty entries. Adding a tour later is an edit to this file and some `data-tour`
 * attributes on the page, and nothing else.
 *
 * Copy in this file is the substance of the feature. The machinery around it is a
 * composable and a storage module; this is the part that has to be right about the domain
 * the tours exist to explain.
 */

export interface TourStep {
	/** Matches a `data-tour="…"` attribute on the page. Never a class selector. */
	anchor: string;
	title: string;
	body: string;
	/**
	 * The route this step points at, when it points at something that navigates. The step
	 * is dropped when the operator cannot reach that route.
	 *
	 * Deliberately a route and never a role. A step naming a role would hardcode an answer
	 * that belongs to the destination, which is the defect `add-route-aware-link-gating`
	 * removed from nine files. Declaring the route means the step and the link it describes
	 * can never disagree, and a change to the route's `minRole` needs no edit here.
	 */
	route?: { name: string };
}

export interface Tour {
	key: string;
	/**
	 * Raise this when the copy or the page changes materially, and operators who saw the
	 * older version see the new one once. Leave it alone for a typo.
	 */
	version: number;
	steps: TourStep[];
}

/**
 * Shown once ever, on the first depot page an operator reaches, then never again.
 *
 * Every anchor here is outside the `RouterView` in `layouts/depot.vue`. That is a hard
 * constraint rather than a preference: the `RouterView` is keyed on `$route.fullPath`, so
 * anything inside it is destroyed and rebuilt on every navigation, and a shell tour
 * anchored to page content would break the moment it was chained into a page tour.
 */
export const SHELL_TOUR: Tour = {
	key: 'shell',
	version: 1,
	steps: [
		{
			anchor: 'shell-nav',
			title: 'Everything lives here',
			body:
				'Depot is grouped by what you are working on: companies and their sites, the ' +
				'profiles that collect reviews, and the scheduled jobs behind both. Only the ' +
				'sections your account can reach are shown.',
		},
		{
			anchor: 'shell-search',
			title: 'Jump anywhere',
			body:
				'Press Cmd K, or Ctrl K, to search every page by name. It is usually faster ' +
				'than the menu once you know what a page is called.',
		},
		{
			anchor: 'shell-breadcrumb',
			title: 'Where you are',
			body:
				'The trail shows the path to this page, and every part of it is clickable. ' +
				'Depot nests deeply, so this is the quickest way back up.',
		},
		{
			// Rendered only for accounts that can preview roles, so it disappears from the
			// tour on its own. See the presence rule in useDepotTour.
			anchor: 'shell-role-preview',
			title: 'See what others see',
			body:
				'Preview the site as a lower-privileged role, to check what a colleague can ' +
				'reach before you send them a link. It changes the interface only: your own ' +
				'access is unaffected.',
		},
		{
			anchor: 'shell-tour-button',
			title: 'This button brings the tours back',
			body:
				'Each page explains itself the first time you open it. Use this to replay the ' +
				'tour for the page you are on, at any time, including after you have dismissed ' +
				'them all.',
		},
	],
};

/**
 * Page tours, keyed by route NAME rather than by path. A parameterised route such as the
 * company detail page is one page with many records, so keying on path would re-fire its
 * tour for every company anyone ever opened.
 */
const D = ROUTE_NAMES.DEPOT;

const page = (key: string, steps: TourStep[]): Tour => {
	return { key, version: 1, steps };
};

export const PAGE_TOURS: Record<string, Tour> = {
	[D.OVERVIEW]: page(D.OVERVIEW, [
		{
			anchor: 'page-header',
			title: 'The depot dashboard',
			body:
				'A live view of what the estate is doing: how many profiles exist, where reviews ' +
				'are coming from, and anything currently needing attention.',
		},
		{
			anchor: 'page-main',
			title: 'Overview and operations',
			body:
				'Overview is the health of the data. Operations is the health of the jobs that ' +
				'produce it, so it is the tab to open when something looks stale.',
		},
	]),

	[D.COMPANIES.DASHBOARD]: page(D.COMPANIES.DASHBOARD, [
		{
			anchor: 'page-header',
			title: 'Companies',
			body:
				'A company owns sites, and sites own the profiles that collect reviews. Almost ' +
				'everything in depot hangs off this hierarchy.',
		},
		{
			anchor: 'page-main',
			title: 'Browsing and maintenance are separate',
			body:
				'The first card lists companies. The rest are maintenance jobs that change data, ' +
				'so they are shown only to accounts allowed to run them.',
		},
		{
			// Declares the route it points at, so operators who cannot reach the Add company
			// page never see this step. The step and the card it describes are gated by the
			// same route meta, so they cannot disagree.
			anchor: 'companies-add',
			route: { name: ROUTE_NAMES.DEPOT.COMPANIES.ADD_COMPANY },
			title: 'Adding a company',
			body:
				'A new company starts here. It needs its sites and their channels before any ' +
				'reviews will arrive for it, so this is the first of three steps rather than all ' +
				'of them.',
		},
	]),

	[D.COMPANIES.OVERVIEW]: page(D.COMPANIES.OVERVIEW, [
		{
			anchor: 'page-header',
			title: 'Every company',
			body: 'Search and filter the full list. Your search and filters are kept as you navigate away and back.',
		},
		{
			anchor: 'page-main',
			title: 'Open a company to go deeper',
			body:
				'A row opens the company, from which you reach its sites, and from a site its ' +
				'profiles. Row menus carry the actions your role permits.',
		},
	]),

	[D.COMPANY.VIEW_COMPANY]: page(D.COMPANY.VIEW_COMPANY, [
		{
			anchor: 'page-header',
			title: 'One company',
			body: 'Everything recorded against this company, and the jobs that can be run for all of its sites at once.',
		},
		{
			anchor: 'page-main',
			title: 'Where to go next',
			body:
				'These cards lead to the company\'s sites and its settings. Company-wide jobs ' +
				'announce what they will touch before they run.',
		},
	]),

	[D.COMPANY.VIEW_SITES]: page(D.COMPANY.VIEW_SITES, [
		{
			anchor: 'page-header',
			title: 'Sites in this company',
			body: 'A site is a physical location. Reviews and place details are attached to sites, not to the company.',
		},
		{
			anchor: 'page-main',
			title: 'Filter before you scroll',
			body:
				'Large companies run to hundreds of sites, so the filters above the table are ' +
				'usually faster than scrolling. Inactive sites are marked rather than hidden.',
		},
	]),

	[D.COMPANY.SITES.VIEW_SITE]: page(D.COMPANY.SITES.VIEW_SITE, [
		{
			anchor: 'page-header',
			title: 'One site',
			body: 'The location itself, the channels feeding it reviews, and the Google place record behind it.',
		},
		{
			anchor: 'page-main',
			title: 'Channels are the link to reviews',
			body:
				'Each channel connects this site to one review source. A site with no channels ' +
				'collects nothing, which is the usual explanation for a site with no reviews.',
		},
	]),

	[D.PROFILES.VIEW_PROFILES]: page(D.PROFILES.VIEW_PROFILES, [
		{
			anchor: 'page-header',
			title: 'Profiles',
			body:
				'A profile is one listing on one review platform. Sites have profiles; profiles ' +
				'have reviews.',
		},
		{
			anchor: 'page-main',
			title: 'Search by whichever field you have',
			body:
				'Profiles can be found by name, by the site or company they belong to, or by ' +
				'platform identifier when that is all you have.',
		},
	]),

	[D.PROFILE.VIEW_PROFILE]: page(D.PROFILE.VIEW_PROFILE, [
		{
			anchor: 'page-header',
			title: 'One profile',
			body: 'What this listing is, which site owns it, and how many reviews have been collected from it.',
		},
		{
			anchor: 'page-main',
			title: 'Reviews, insights and parentage',
			body:
				'From here you can read the reviews themselves, run an AI summary of them, or ' +
				'follow the profile up to its site and company.',
		},
	]),

	[D.REVIEWS.DASHBOARD]: page(D.REVIEWS.DASHBOARD, [
		{
			anchor: 'page-header',
			title: 'Reviews',
			body: 'Everything collected across every platform, and the tools for searching and summarising it.',
		},
		{
			anchor: 'page-main',
			title: 'Search or summarise',
			body:
				'Search finds individual reviews. Insights summarises many at once, which is the ' +
				'better starting point when you do not know what you are looking for.',
		},
	]),

	[D.REVIEWS.OVERVIEW]: page(D.REVIEWS.OVERVIEW, [
		{
			anchor: 'page-header',
			title: 'All reviews',
			body:
				'Every review collected across every platform, in one list. This is the place to ' +
				'find a specific review; Insights is the place to summarise many at once.',
		},
		{
			anchor: 'page-main',
			title: 'Narrow before you search',
			body:
				'Filter by company, site or platform first. The free-text search runs across the ' +
				'review body, so on the full estate it is far more useful once the set is ' +
				'already narrowed.',
		},
	]),

	[D.REVIEWS.INSIGHTS]: page(D.REVIEWS.INSIGHTS, [
		{
			anchor: 'page-header',
			title: 'AI insights',
			body: 'Summarise what reviews say about a company, a site or a single profile, over a period you choose.',
		},
		{
			anchor: 'page-main',
			title: 'Narrow the scope for fuller coverage',
			body:
				'A whole company pools every review into one corpus and only as much as the token ' +
				'budget allows is analysed. Narrowing to a site, or shortening the period, covers ' +
				'more of what is there.',
		},
	]),

	[D.PLACE_DETAILS.OVERVIEW]: page(D.PLACE_DETAILS.OVERVIEW, [
		{
			anchor: 'page-header',
			title: 'Place details',
			body: 'The Google Place record behind each site: address, coordinates, opening hours and ratings.',
		},
		{
			anchor: 'page-main',
			title: 'Reading and writing are separated',
			body:
				'Browsing, searching and the map only read. Refresh, backfill and import change ' +
				'stored data, so they appear only for accounts allowed to run them.',
		},
	]),

	[D.PLACE_DETAILS.MAP]: page(D.PLACE_DETAILS.MAP, [
		{
			anchor: 'page-header',
			title: 'The place details map',
			body: 'Plot stored place records geographically rather than reading them as rows.',
		},
		{
			anchor: 'page-main',
			title: 'Four ways to choose what to plot',
			body:
				'By company, by address search, by a single Place ID, or by an area you click on ' +
				'the map. Nothing is fetched until you run a query.',
		},
	]),
};

export const tourForRoute = (routeName: string | null | undefined): Tour | undefined => {
	return routeName ? PAGE_TOURS[routeName] : undefined;
};
